import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  VNextToast,
  type VNextToastNotification,
} from "@/vnext/primitives/VNextToast";

type Notify = (notification: Omit<VNextToastNotification, "id">) => void;

const VNextToastContext = createContext<Notify | null>(null);

let nextToastId = 0;

/**
 * F-221 to F-223: one confirmation at a time, bottom left, for the whole
 * shell. A newer confirmation replaces the one showing.
 */
export function VNextToastProvider({ children }: { children: ReactNode }) {
  const [notification, setNotification] =
    useState<VNextToastNotification | null>(null);
  const notify = useCallback<Notify>((next) => {
    nextToastId += 1;
    setNotification({
      ...next,
      id: `toast-${nextToastId}`,
    });
  }, []);
  const dismiss = useCallback(() => setNotification(null), []);
  const value = useMemo(() => notify, [notify]);
  return (
    <VNextToastContext.Provider value={value}>
      {children}
      <VNextToast notification={notification} onDismiss={dismiss} />
    </VNextToastContext.Provider>
  );
}

const noop: Notify = () => undefined;

/**
 * Shows a confirmation (F-221) or a failure (F-223). Outside the shell (for
 * example an isolated test render) it does nothing. Messages must be
 * authored, safe copy: never a server error's raw message.
 */
export const useVNextToast = (): Notify =>
  useContext(VNextToastContext) ?? noop;
